import { useEffect, useState, useCallback } from 'react'
import { apiCupApply, apiCupCancel, apiCupListMy, apiChampionsApply } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface CupEntry {
  ENTRY_CODE: number
  CUP_CODE:   number
  TIER:       number
  CUP_TYPE?:  number
  EVENT_DATE: string
  STATUS:     number
  ELIM_ROUND: number | null
  PRIZE:      number
  CHAMPION_CLUB: number | null
  CHAMPION_NAME: string | null
}

interface Props { auth: AuthState; tier: number; championsQualified?: number; nonce?: number }

const ROUND_LABEL: Record<number, string> = { 1: '16강 탈락', 2: '8강 탈락', 3: '4강 탈락', 4: '준우승' }

function todayKst(): string {
  const now = new Date()
  // toLocaleDateString로 한국 표준 시간 사용
  const k = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' })
  return k.format(now)
}

export default function Cup({ auth, tier, championsQualified, nonce }: Props) {
  const [rows, setRows] = useState<CupEntry[]>([])
  const [actionMsg, setActionMsg] = useState<{text: string, ok: boolean} | null>(null)

  const reload = useCallback(async () => {
    const r = await apiCupListMy(auth.token)
    if (r.resultCode === '00') {
      try { const obj = JSON.parse(r.resultData); setRows(obj.rows ?? []) } catch {}
    }
  }, [auth.token])

  useEffect(() => { reload() }, [reload, nonce])

  async function handleApply() {
    const date = todayKst()
    if (!window.confirm(`오늘(${date}) ${tier}부 컵에 참가 신청합니다.\n\n• 마감: 매일 22:00 KST\n• 22:00 시점에 자동 매칭·진행 (스쿼드 자동 사용)\n• 결과만 확인 가능\n\n진행할까요?`)) return
    const r = await apiCupApply(auth.token, date)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') reload()
  }

  async function handleChampionsApply() {
    const date = todayKst()
    if (!window.confirm(`오늘(${date}) 챔피언스리그에 참가 신청합니다.\n\n• 직전 시즌 1부 1~4등 자격\n• 마감: 매일 22:00 KST\n• 24팀 → 16팀 추첨, NPC는 1부 클럽\n• 차등 상금: 우승 500억 / 준 200억 / 4강 100억 / 8강 50억 / 16강 30억\n\n진행할까요?`)) return
    const r = await apiChampionsApply(auth.token, date)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') reload()
  }

  async function handleCancel(e: CupEntry) {
    if (!window.confirm(`${e.EVENT_DATE.slice(0, 10)} ${e.TIER}부 컵 신청을 취소합니다.`)) return
    const r = await apiCupCancel(auth.token, e.CUP_CODE)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') reload()
  }

  return (
    <div className="page-pad">
      <h2>컵대회</h2>
      <section className="card">
        <p className="muted">
          매일 22:00 KST에 같은 등급 사용자끼리 매칭됩니다 (24팀 → 16팀 토너먼트).<br/>
          신청 시 활성 전술이 그대로 사용되며, 결과는 다음날 확인할 수 있습니다.
        </p>
        <button className="btn-buy" onClick={handleApply}>오늘 컵 참가 신청 ({tier}부)</button>
        {championsQualified === 1 && (
          <button className="btn-buy" style={{marginLeft:8, background:'#7e57c2'}} onClick={handleChampionsApply}>
            🏆 챔피언스리그 신청
          </button>
        )}
        {actionMsg && <span className={actionMsg.ok ? 'msg ok' : 'msg'} style={{marginLeft:12}}>{actionMsg.text}</span>}
      </section>

      {championsQualified === 1 && (
        <section className="card" style={{borderLeft:'4px solid #7e57c2'}}>
          <h3>🏆 챔피언스리그 자격 보유</h3>
          <p className="muted">
            직전 시즌 1부 1~4등을 달성하여 챔피언스리그 신청 자격이 있습니다.<br/>
            상금: 우승 ₩500억 · 준우승 ₩200억 · 4강 ₩100억 · 8강 ₩50억 · 16강 ₩30억 (참가 전원)
          </p>
        </section>
      )}

      <section className="card">
        <h3>내 컵 신청·결과</h3>
        <table className="data-table">
          <thead><tr><th>날짜</th><th>등급</th><th>상태</th><th>결과</th><th>상금</th><th>우승팀</th><th></th></tr></thead>
          <tbody>
            {rows.map(e => (
              <tr key={e.ENTRY_CODE}>
                <td>{e.EVENT_DATE.slice(0, 10)}</td>
                <td>{e.CUP_TYPE === 1 ? '🏆 챔스' : `${e.TIER}부`}</td>
                <td>{e.STATUS === 0 ? '⏳ 신청 대기' : '✅ 종료'}</td>
                <td>{
                  e.STATUS === 0 ? '-' :
                  e.PRIZE > 0 ? '🏆 우승!' :
                  e.ELIM_ROUND ? ROUND_LABEL[e.ELIM_ROUND] : '-'
                }</td>
                <td className="num">{e.PRIZE > 0 ? `₩ ${e.PRIZE.toLocaleString()}` : '-'}</td>
                <td>{e.CHAMPION_NAME ?? '-'}</td>
                <td>{e.STATUS === 0 ? <button className="btn-cancel" onClick={() => handleCancel(e)}>취소</button> : null}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="muted">신청 내역 없음</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  )
}
